/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import { ArrowDown } from "components/Icons";
import {
  ComponentProps,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import useDetectClickOutside from "utils/useDetectClickOutside";
import { Input } from "../Input";
import { LoadingDots } from "../Loaders";
import Stack from "../Stack";
import Text from "../Text";
import countries_codes from "./codes";
import flagPosition from "./telephone.module.css";
import { verifyOnlyPositives } from "utils/validator";

const PhoneInput = ({
  isMissing,
  countryCode,
  ...props
}: { isMissing?: boolean; countryCode?: string } & ComponentProps<
  typeof Input
>) => {
  const countryCodePrefix = countryCode ? `+${countryCode}` : "";

  return (
    <div className="relative w-full">
      {countryCodePrefix && (
        <span
          className={cn(
            "absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none select-none",
            "-text--component-colors-field-disabled-description",
            "text-base leading-6 opacity-70",
          )}
        >
          {countryCodePrefix}
        </span>
      )}
      <Input
        {...props}
        inputClassName={cn(
          "!mt-0",
          countryCodePrefix && "ps-14",
          props.inputClassName,
        )}
        placeholder="Phone Number"
        error={isMissing}
      />
    </div>
  );
};

type CountryCodeInputProps = {
  value: string;
  onChange: Function;
  disabled?: boolean;
  isMissing?: boolean;
};

type IOption = {
  label: string;
  value: string;
  id: string;
};

const CountryCodeInput: FC<CountryCodeInputProps> = ({
  value,
  onChange,
  disabled,
  isMissing,
}) => {
  const boxRef = useRef(null);
  useDetectClickOutside(boxRef, () => setOpen(false));
  const [isOpen, setOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption>({
    label: "United States",
    value: "1",
    id: "US",
  });
  useEffect(() => {
    if (value === selectedOption.value) return;
    if (value && typeof value === "string") {
      const _value = value.replace("+", "");
      const target = countries_codes.find(
        (country) =>
          country.value === _value ||
          country.label === _value ||
          country.id === _value,
      );
      if (target) {
        setSelectedOption(target);
      }
    }
  }, [value]);
  const onSelect = (option: IOption) => {
    if (option.id === selectedOption?.id) return;
    setSelectedOption(option);
    setOpen(false);
    if (onChange) {
      onChange(option.value);
    }
  };
  const [isSearching, setSearching] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const prevSearch = useRef("");
  const onSearch = (search: string) => {
    if (prevSearch.current === search) return;
    setSearching(true);
    timeoutRef.current = setTimeout(async () => {
      prevSearch.current = search;
      const _filteredOptions = countries_codes.filter((option) => {
        const _label = option.label.toLowerCase();
        const _value = option.value.toLowerCase();
        const _id = option.id.toLowerCase();
        const _search = search.toLowerCase();
        return (
          _label.includes(_search) ||
          _value.includes(_search) ||
          _id.includes(_search)
        );
      });
      setFilteredOptions(_filteredOptions);
      setSearching(false);
    }, 200);
  };
  return (
    <div ref={boxRef} className={"w-[75px] shrink-0"}>
      <div
        className={cn(
          "relative flex items-center w-full border bg-transparent -text--component-colors-field-filled-input-text h-[45px] ps-1 pe-6 py-2.5 -rounded-input cursor-pointer",

          isMissing
            ? "-border--component-colors-field-error-input-border"
            : "-border--component-colors-field-default-input-border",
        )}
        onClick={() => {
          if (disabled) return;
          setOpen(!isOpen);
        }}
      >
        <div className="ms-1 text-sm leading-5 select-none">
          {selectedOption?.value ? `+${selectedOption.value}` : ""}
        </div>

        <div className="absolute end-2 top-4 ">
          <ArrowDown size="sm" />
        </div>
      </div>
      <Stack className="relative">
        {isOpen && (
          <ul
            className={cn(
              "w-80 border-light-gray bg-primary transition-all duration-200 absolute z-[10] list-none max-h-64 overflow-auto border-2 py-1",
            )}
          >
            <Stack as="li" className="sticky -top-1 -mt-1">
              <Input
                type="text"
                placeholder="Search..."
                inputClassName={
                  "!mt-0 text-white bg-primary placeholder:text-white"
                }
                onKeyUp={(e: KeyboardEvent) => {
                  const target = e.target as HTMLInputElement;
                  clearTimeout(timeoutRef.current);
                  if (target.value.length === 0) {
                    setFilteredOptions([]);
                    setSearching(false);
                    return;
                  }
                  onSearch(target.value);
                }}
              />
              <div
                className={cn("absolute end-8 top-4", !isSearching && "hidden")}
              >
                <LoadingDots />
              </div>
            </Stack>
            {filteredOptions.length > 0
              ? filteredOptions.map(({ id, value, label }) => {
                  return (
                    <li
                      key={id}
                      className={cn(
                        "py-1 px-3 flex gap-x-s15 justify-between bg-transparent hover:bg-primary-800",
                      )}
                      role="button"
                      onClick={() => onSelect({ id, value, label })}
                    >
                      <div className="flex gap-x-s15 items-center">
                        <div
                          className={cn(
                            flagPosition.flag,
                            flagPosition[id.toLowerCase()],
                          )}
                        />
                        <Text>{label}</Text>
                      </div>
                      <Text className="ms-auto">
                        {selectedOption &&
                          selectedOption.value === value &&
                          "✓"}
                      </Text>
                    </li>
                  );
                })
              : countries_codes.map(({ id, value, label }) => {
                  return (
                    <li
                      key={id}
                      className={cn(
                        "py-1 px-3 flex gap-x-s15 justify-between bg-transparent hover:bg-primary-800",
                      )}
                      role="button"
                      onClick={() => onSelect({ id, value, label })}
                    >
                      <div className="flex gap-x-s15 items-center">
                        <div
                          className={cn(
                            flagPosition.flag,
                            flagPosition[id.toLowerCase()],
                          )}
                        />
                        <Text>{label}</Text>
                      </div>
                      <Text className="ms-auto">
                        {selectedOption &&
                          selectedOption.value === value &&
                          "✓"}
                      </Text>
                    </li>
                  );
                })}
          </ul>
        )}
      </Stack>
    </div>
  );
};

const MyPhoneInput = ({
  countryCode = "",
  setCountryCode = (code: string) => {},
  value = "",
  onChange = (value: string) => {},
  disabled = false,
  isMissing = false,
  isCodeMissing = false,
}) => {
  useEffect(() => {
    if (!countryCode) {
      setCountryCode("1");
    }
  }, [countryCode]);

  return (
    <div className="flex items-center gap-x-s10 [direction:ltr]">
      <CountryCodeInput
        isMissing={isCodeMissing}
        value={countryCode}
        onChange={(code: string) => setCountryCode(code)}
        disabled={disabled}
      />
      <Stack>
        <PhoneInput
          isMissing={isMissing}
          type="tel"
          value={value}
          maxLength={15}
          countryCode={countryCode}
          onChange={(e: any) => {
            const { value } = e.target;
            const numbersOnly = verifyOnlyPositives(value);
            //If we want to disallow leading zeros in the future, uncomment the following line
            // const normalized = numbersOnly.replace(/^0+/, "");
            // onChange(normalized);
            onChange(numbersOnly);
          }}
          disabled={disabled}
        />
      </Stack>
    </div>
  );
};

export default MyPhoneInput;
